/**
 * 訂單進度總覽 Progress Overview — the OCC landing lens (S4.3; charter, plan
 * conflict #5).
 *
 * One row per SO, rolled up from its FulfillmentLines by the SHARED
 * selectors (`selectOrderRollups` — served by `/occ/board`), so every number
 * equals the source stores by construction. Node-stage chips render the
 * late-bound `FulfillmentNodeConfig` labels — never raw enum codes. ETA is a
 * read-through join to the shipping store's 裝櫃批次 rows. Row click drills
 * into the 訂單明細 lens pre-filtered to the SO.
 */

import { Badge, Progress, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { BalanceValue } from '@/components/common/QuantityBucketBar';
import { useOccBoard, useOccDocIndex, useOccLines, useOccNodeConfigs } from '@/hooks/useOcc';
import { useOccStore } from '@/stores/occStore';
import type { OccOrderRollupRow } from '@/lib/occ/selectors';
import { LensFooter, LensUnavailableNotice, SeverityTag } from '../lensShared';

const { Text } = Typography;

const OverviewLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();

  const { orders, syncedAt, isLoading, error, refresh, unavailable } = useOccBoard();
  const { lines } = useOccLines();
  const { nodeLabel } = useOccNodeConfigs();
  const { etaForLine } = useOccDocIndex();
  const { setLinesFilters, resetLinesFilters } = useOccStore();

  /** Earliest batch ETA across the SO's lines (read-through join, may be absent). */
  const etaForSo = (soId: string): string | undefined => {
    const etas = lines
      .filter((line) => line.soId === soId)
      .map((line) => etaForLine(line.id))
      .filter((eta): eta is string => Boolean(eta))
      .sort();
    return etas[0];
  };

  const drillToLines = (row: OccOrderRollupRow) => {
    resetLinesFilters();
    setLinesFilters({ search: row.soNumber });
    navigate('/auth/occ/lines');
  };

  const columns: TableProps<OccOrderRollupRow>['columns'] = [
    {
      title: t('occ:occ.overview.columns.so'),
      key: 'so',
      width: 220,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{row.soNumber}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {row.customerName}
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.overview.columns.lines'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('occ:occ.overview.columns.nodes'),
      key: 'nodes',
      width: 200,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {row.nodes.map((code) => (
            <Tag key={code} style={{ margin: 0 }}>
              {nodeLabel(code) || renderDash()}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: t('occ:occ.overview.columns.receivedPct'),
      key: 'receivedPct',
      width: 220,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5" style={{ maxWidth: 200 }}>
          {/* % text + qty fraction are the non-colour channels; bar caps at 100. */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <Progress
              percent={Math.min(100, row.receivedPct)}
              showInfo={false}
              size="small"
              style={{ flex: 1, margin: 0 }}
            />
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: token.fontSizeSM }}>
              {row.receivedPct}%
            </span>
          </div>
          <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {t('occ:occ.overview.receivedOfOrdered', {
              received: row.qtyReceived,
              ordered: row.qtyOrdered,
            })}
          </Text>
        </div>
      ),
    },
    {
      title: t('occ:occ.overview.columns.orderBalance'),
      key: 'orderBalance',
      width: 110,
      align: 'right',
      render: (_: unknown, row) => (
        <BalanceValue
          value={row.orderBalance}
          warningTitle={t('occ:occ.buckets.negativeWarning')}
        />
      ),
    },
    {
      title: t('occ:occ.overview.columns.eta'),
      key: 'eta',
      width: 120,
      render: (_: unknown, row) => renderDash(etaForSo(row.soId)),
    },
    {
      title: t('occ:occ.overview.columns.exceptions'),
      key: 'exceptions',
      width: 130,
      render: (_: unknown, row) =>
        row.exceptionCount === 0 ? (
          renderDash()
        ) : (
          <div className="tw-flex tw-items-center tw-gap-2">
            {row.maxSeverity && <SeverityTag severity={row.maxSeverity} />}
            <Badge count={row.exceptionCount} color={token.colorError} />
          </div>
        ),
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="overview" />
      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccOrderRollupRow>
          rowKey="soId"
          dataSource={orders}
          columns={columns}
          loading={isLoading}
          onRow={(row) => ({
            onClick: () => drillToLines(row),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noOrders')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1080 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />
    </>
  );
};

export default OverviewLens;
